import axios from 'axios'
import { v4 } from 'uuid'

import {
  abortMultipartUpload,
  callPresignedUrl,
  completeMultipartUpload,
  createPresignedUrl,
  initiateMultipartUpload,
  validateUploadId,
} from 'api/storage'
import constants from '../../../../constants'
import i18n from '../../../../i18n'
import { getMediaDimensions } from 'utils/helpers'
import { insertMediaItems } from './mediaPayload'
import { saveLibraryImages } from 'api/library'

const PART_SIZE = 20 * 1000 * 1000 //20MB

const buildReplaceFilePayload = (file, fileToReplace) => ({
  file,
  fileToReplace,
})

const parseReplaceFileAction = (action, state) => {
  const payload = action.payload

  if (payload?.fileToReplace && payload?.file) {
    return {
      newFile: payload.file,
      fileToReplace: payload.fileToReplace,
    }
  }

  return {
    newFile: payload,
    fileToReplace: state.fileToReplace,
  }
}

// A file without a sub_group_id (e.g. a YouTube video — see
// ensureSharedSubGroupIds in mediaPayload.js) forms its own single-item
// group keyed by its own id (see resolveGroupKey in buildMediaListItems.js),
// so it must match on id instead of sub_group_id here.
const isFileInCoCreateGroup = (file, groupId) =>
  !!file && (file.sub_group_id != null ? file.sub_group_id === groupId : file.id === groupId)

const shouldTrackReplaceRollback = (newFile) =>
  newFile?.status === fileStatus.new || newFile?.status === fileStatus.uploading

const pruneFailedReplacesByMedia = (failedReplaceByNewId, media) => {
  const mediaIds = new Set((media ?? []).map((file) => String(file.id)))

  return Object.fromEntries(
    Object.entries(failedReplaceByNewId ?? {}).filter(
      ([newFileId, entry]) =>
        mediaIds.has(String(newFileId)) || mediaIds.has(String(entry?.oldFile?.id)),
    ),
  )
}

const removeFailedReplaceEntriesByOldId = (failedReplaceByNewId, oldFileId) =>
  Object.fromEntries(
    Object.entries(failedReplaceByNewId ?? {}).filter(
      ([, entry]) => entry?.oldFile?.id !== oldFileId,
    ),
  )

export const MEDIA_ASPECT_RATIOS = ['1:1', '4:5', '1.91:1', '9:16']
export const MEDIA_SLOT_RATIOS = ['1:1', '9:16']

const REEL_PLACEMENT_THRESHOLD = 1.4

export const parseRatioLabel = (ratioLabel) => {
  const [widthPart, heightPart] = ratioLabel.split(':').map(Number)

  if (!widthPart || !heightPart) {
    return null
  }

  return widthPart / heightPart
}

export const findClosestRatioLabel = (width, height, ratioLabels) => {
  if (!width || !height) {
    return null
  }

  const aspectRatio = width / height

  return ratioLabels.reduce((closestRatio, ratioLabel) => {
    const targetRatio = parseRatioLabel(ratioLabel)
    const closestTargetRatio = parseRatioLabel(closestRatio)

    if (targetRatio == null || closestTargetRatio == null) {
      return closestRatio
    }

    return Math.abs(aspectRatio - targetRatio) < Math.abs(aspectRatio - closestTargetRatio)
      ? ratioLabel
      : closestRatio
  }, ratioLabels[0])
}

export const inferPlacementType = (file) => {
  if (!file?.width || !file?.height) {
    return null
  }

  const aspectRatio = file.height / file.width
  return aspectRatio > REEL_PLACEMENT_THRESHOLD ? 'reel' : 'feed'
}

export const computeAspectRatio = (width, height) =>
  findClosestRatioLabel(width, height, MEDIA_ASPECT_RATIOS)

export const computeSlotRatio = (width, height) => {
  const placementType = inferPlacementType({ width, height })
  return placementType === 'reel' ? '9:16' : '1:1'
}

export const aspectRatioLabelToSlotRatio = (ratioLabel) => (ratioLabel === '9:16' ? '9:16' : '1:1')

export const resolveImageSlotRatio = (image) => {
  if (!image) {
    return null
  }

  if (image.placement_type === 'reel') {
    return '9:16'
  }

  if (image.placement_type === 'feed') {
    return '1:1'
  }

  if (image.width && image.height) {
    return computeSlotRatio(image.width, image.height)
  }

  if (image.ratio) {
    return aspectRatioLabelToSlotRatio(image.ratio)
  }

  return null
}

export const slotRatioToPlacementType = (slotRatio) => (slotRatio === '9:16' ? 'reel' : 'feed')

export const validatePlacementForSlot = (width, height, requiredSlotRatio) => {
  if (!width || !height || !requiredSlotRatio) {
    return false
  }

  const uploadedPlacementType = inferPlacementType({ width, height })
  const requiredPlacementType = slotRatioToPlacementType(requiredSlotRatio)

  return uploadedPlacementType === requiredPlacementType
}

// True when requiredSlotRatios is exactly the legacy Meta feed/reel pair
// (any order) — used to keep that path on the untouched threshold-based
// logic above instead of nearest-match, since the two aren't equivalent
// (see resolveImageSlotRatioForRequired).
const isLegacyMetaSlotSet = (requiredSlotRatios) =>
  Array.isArray(requiredSlotRatios) &&
  requiredSlotRatios.length === MEDIA_SLOT_RATIOS.length &&
  MEDIA_SLOT_RATIOS.every((ratio) => requiredSlotRatios.includes(ratio))

// Generalized slot resolver. The legacy 2-slot (Meta-only) required set
// delegates to resolveImageSlotRatio unchanged — it uses a feed/reel
// placement-type threshold, not nearest-match, and the two produce
// different results for some real-world ratios (e.g. an ordinary 3:4
// portrait photo), so they aren't interchangeable. Any other required set
// (Google-inclusive, 3 or 4 exact slots) uses true nearest-match against the
// full list.
//
// width/height are checked before the stored .ratio label. .ratio is itself
// a nearest-match against the fixed 4-way MEDIA_ASPECT_RATIOS bucket list
// (computeAspectRatio), so it's already lossy — re-matching that rounded
// label against a *different* requiredSlotRatios set (e.g. Google Display,
// which excludes 4:5) can pick the wrong slot for images whose real pixel
// ratio sits near a boundary the label collapsed away. Raw width/height
// avoids that double-rounding and is only unavailable for a handful of
// already-hosted items that carry .ratio with no dimensions.
export const resolveImageSlotRatioForRequired = (image, requiredSlotRatios) => {
  if (!image) {
    return null
  }

  if (isLegacyMetaSlotSet(requiredSlotRatios)) {
    return resolveImageSlotRatio(image)
  }

  if (image.width && image.height) {
    return findClosestRatioLabel(image.width, image.height, requiredSlotRatios)
  }

  if (image.ratio) {
    const parsed = parseRatioLabel(image.ratio)
    if (parsed != null) {
      return findClosestRatioLabel(parsed, 1, requiredSlotRatios)
    }
  }

  return null
}

// Shared nearest-match validator for a newly uploaded/selected file. Legacy
// Meta-only required set defers to the existing bucket check
// (validatePlacementForSlot); any other required set accepts the file into
// targetSlotRatio only if that's genuinely its nearest slot in the full list
// — soft/nearest-match, not strict equality, per product requirement.
export const isFileRatioValidForSlot = (width, height, targetSlotRatio, requiredSlotRatios) => {
  if (isLegacyMetaSlotSet(requiredSlotRatios)) {
    return validatePlacementForSlot(width, height, targetSlotRatio)
  }

  if (!width || !height || !targetSlotRatio) {
    return false
  }

  return findClosestRatioLabel(width, height, requiredSlotRatios) === targetSlotRatio
}

export const buildMediaSlots = (images = [], requiredSlotRatios, { excludeRatio = null } = {}) => {
  const imagesBySlot = new Map()

  images.forEach((image) => {
    const slotRatio = resolveImageSlotRatioForRequired(image, requiredSlotRatios)

    if (slotRatio && !imagesBySlot.has(slotRatio)) {
      imagesBySlot.set(slotRatio, image)
    }
  })

  const excludeSlotRatio = excludeRatio
    ? resolveImageSlotRatioForRequired({ ratio: excludeRatio }, requiredSlotRatios)
    : null
  const slotRatios = excludeSlotRatio
    ? requiredSlotRatios.filter((ratio) => ratio !== excludeSlotRatio)
    : requiredSlotRatios

  return slotRatios.map((ratio) => ({
    ratio,
    image: imagesBySlot.get(ratio) ?? null,
  }))
}

const EXACT_RATIO_TOLERANCE = 0.01

// Google's exact-ratio rules only ever apply to images — Google doesn't
// support uploaded video at all (only YouTube references), so an uploaded
// video sitting in a ratio slot should never be flagged/counted by any of
// the exact-ratio checks below, regardless of its own width/height.
export const isImageMedia = (item) => Boolean(item?.type?.startsWith('image/'))

// Google requires an exact ratio match, unlike Meta's nearest-match slots.
// Prefers raw width/height over image.ratio — ratio is itself produced by
// computeAspectRatio (nearest-match against the same canonical labels), so
// comparing labels alone would trivially always "match" whatever slot
// nearest-match already picked. width/height reflects the real pixel ratio;
// .ratio is only a last-resort fallback for items without dimensions.
export const isExactRatioMatch = (image, targetRatio) => {
  const target = parseRatioLabel(targetRatio)

  if (!image || target == null) {
    return false
  }

  if (image.width && image.height) {
    return Math.abs(image.width / image.height - target) / target <= EXACT_RATIO_TOLERANCE
  }

  if (image.ratio) {
    const actual = parseRatioLabel(image.ratio)
    return actual != null && Math.abs(actual - target) / target <= EXACT_RATIO_TOLERANCE
  }

  return false
}

// Google requires a dedicated exact-1:1 image and a dedicated exact-1.91:1
// image in the same creative — checked against the flat media array
// directly (not per-slot), since it's about presence of both exact assets
// anywhere in the set, regardless of which slot nearest-match assigns them to,
// and regardless of which sub-group/creative they happen to live in.
export const getMissingGoogleExactRatios = (media = []) =>
  ['1:1', '1.91:1'].filter(
    (ratio) => !media.some((item) => isImageMedia(item) && isExactRatioMatch(item, ratio)),
  )

export const hasRequiredGoogleExactRatios = (media = []) =>
  getMissingGoogleExactRatios(media).length === 0

export const fileStatus = Object.freeze({
  new: 'NEW',
  uploading: 'UPLOADING',
  success: 'SUCCESS',
  uploadFailed: 'UPLOAD_FAILED',
  invalid: 'INVALID',
})

export const initialState = {
  media: [],
  isLoading: false,
  success: null,
  error: null,
  valid: null,
  fileToReplace: null,
  failedReplaceByNewId: {},
  generatedMediaCount: 0,
  eventId: null,
  tc: null,
  tcRunId: null,
}

export const actions = Object.freeze({
  addFile: 'ADD_FILE',
  addLibraryMedia: 'ADD_LIBRARY_MEDIA',
  removeFile: 'REMOVE_FILE',
  startReplace: 'START_REPLACE',
  resetReplace: 'RESET_REPLACE',
  replaceFile: 'REPLACE_FILE',
  cancelReplace: 'CANCEL_REPLACE',
  editFileInfo: 'EDIT_FILE_INFO',

  fileUploadInitiate: 'FILE_UPLOAD_INITIATE',
  fileUploadFail: 'FILE_UPLOAD_FAIL',
  fileUploadSuccess: 'FILE_UPLOAD_SUCCESS',
  uploadComplete: 'UPLOAD_COMPLETE',

  addFilesAsync: 'ADD_FILE_ASYNC',
  uploadFilesAsync: 'UPLOAD_FILES_ASYNC',
  uploadCompleteAsync: 'UPLOAD_COMPLETE_ASYNC',
  removeFileAsync: 'REMOVE_FILE_ASYNC',
  replaceFileAsync: 'REPLACE_FILE_ASYNC',
  retryUploadAsync: 'RETRY_UPLOAD_ASYNC',
  addFileToGroupAsync: 'ADD_FILE_TO_GROUP_ASYNC',
  replaceFileInGroupAsync: 'REPLACE_FILE_IN_GROUP_ASYNC',
  removeGroupAsync: 'REMOVE_GROUP_ASYNC',
  removeFromGroupAsync: 'REMOVE_FROM_GROUP_ASYNC',
  removeFiles: 'REMOVE_FILES',
  removeMediaGroup: 'REMOVE_MEDIA_GROUP',
  removeMediaGroupAsync: 'REMOVE_MEDIA_GROUP_ASYNC',
  addFileToCoCreateGroupAsync: 'ADD_FILE_TO_COCREATE_GROUP_ASYNC',
  replaceFileInCoCreateGroupAsync: 'REPLACE_FILE_IN_COCREATE_GROUP_ASYNC',
  removeFileFromCoCreateGroupAsync: 'REMOVE_FILE_FROM_COCREATE_GROUP_ASYNC',
  applyGeneratedVariants: 'APPLY_GENERATED_VARIANTS',
  setMediaGenerating: 'SET_MEDIA_GENERATING',
  clearMediaGenerating: 'CLEAR_MEDIA_GENERATING',
  bulkUpdateMediaItems: 'BULK_UPDATE_MEDIA_ITEMS',
})

export function mediaUploadReducer(state, action) {
  switch (action.type) {
    case actions.addFile: {
      const existingMedia = state.media.filter((x) => x.status !== fileStatus.invalid)
      const newMedia = insertMediaItems(existingMedia, action.payload)

      return {
        ...state,
        media: newMedia,
        error: null,
        valid: newMedia.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.addLibraryMedia: {
      const existingMedia = state.media
      const newMedia = insertMediaItems(existingMedia, action.payload)

      return {
        ...state,
        media: newMedia,
        error: null,
        valid: newMedia.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.removeFile: {
      const fileToRemove = action.payload
      const failedReplaceEntry = state.failedReplaceByNewId?.[fileToRemove.id]

      if (failedReplaceEntry && fileToRemove.status === fileStatus.uploadFailed) {
        const { oldFile } = failedReplaceEntry
        URL.revokeObjectURL(fileToRemove?.preview)

        const media = state.media.map((file) => (file.id === fileToRemove.id ? oldFile : file))

        const { [fileToRemove.id]: removedEntry, ...restFailedReplaces } =
          state.failedReplaceByNewId ?? {}
        void removedEntry

        return {
          ...state,
          media,
          failedReplaceByNewId: pruneFailedReplacesByMedia(restFailedReplaces, media),
          fileToReplace: null,
          error: null,
          valid: media.every(
            (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
          ),
        }
      }

      URL.revokeObjectURL(fileToRemove?.preview)

      const media = state.media.filter((x) => x.id !== fileToRemove.id)

      return {
        ...state,
        media,
        failedReplaceByNewId: pruneFailedReplacesByMedia(state.failedReplaceByNewId, media),
        error: null,
        valid: media.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.removeFiles: {
      const filesToRemove = Array.isArray(action.payload) ? action.payload : [action.payload]

      filesToRemove.forEach((file) => {
        URL.revokeObjectURL(file?.preview)
      })

      const fileIdsToRemove = new Set(filesToRemove.map((f) => f.id))
      const media = state.media.filter((x) => !fileIdsToRemove.has(x.id))

      return {
        ...state,
        media,
        failedReplaceByNewId: pruneFailedReplacesByMedia(state.failedReplaceByNewId, media),
        error: null,
        valid: media.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.applyGeneratedVariants: {
      const media = action.payload

      return {
        ...state,
        media,
        generatedMediaCount: 0,
        failedReplaceByNewId: pruneFailedReplacesByMedia(state.failedReplaceByNewId, media),
        error: null,
        valid: media.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.removeMediaGroup: {
      const { groupId } = action.payload
      const filesToRemove = state.media.filter((file) => isFileInCoCreateGroup(file, groupId))

      filesToRemove.forEach((file) => {
        URL.revokeObjectURL(file?.preview)
      })

      const media = state.media.filter((file) => !isFileInCoCreateGroup(file, groupId))

      return {
        ...state,
        media,
        error: null,
        valid: media.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.startReplace: {
      const fileToReplace = action.payload
      if (!fileToReplace) throw new Error('Invalid file to replace')

      return {
        ...state,
        fileToReplace,
        error: null,
      }
    }
    case actions.cancelReplace: {
      return {
        ...state,
        fileToReplace: null,
      }
    }
    case actions.resetReplace: {
      const { fileToReplace } = state
      if (!fileToReplace) {
        return {
          ...state,
          error: action.payload?.error,
        }
      }

      const media = state.media.map((file) => (file.id === fileToReplace.id ? fileToReplace : file))

      return {
        ...state,
        media,
        fileToReplace: null,
        failedReplaceByNewId: removeFailedReplaceEntriesByOldId(
          state.failedReplaceByNewId,
          fileToReplace.id,
        ),
        error: action.payload?.error,
        valid:
          media.every(
            (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
          ) && !action.payload?.error,
      }
    }
    case actions.replaceFile: {
      const { newFile, fileToReplace } = parseReplaceFileAction(action, state)

      if (!fileToReplace) throw new Error('Invalid file ID')

      const media = state.media.map((file) => (file.id === fileToReplace.id ? newFile : file))

      const staleFailedReplaces = removeFailedReplaceEntriesByOldId(
        state.failedReplaceByNewId,
        fileToReplace.id,
      )

      const failedReplaceByNewId = shouldTrackReplaceRollback(newFile)
        ? {
            ...staleFailedReplaces,
            [newFile.id]: { oldFile: fileToReplace },
          }
        : staleFailedReplaces

      return {
        ...state,
        media,
        failedReplaceByNewId,
        error: null,
        valid: media.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.editFileInfo: {
      const media = state.media
      const { id: fileId } = action.payload
      const file = media.find((x) => x.id === fileId)

      if (!file) throw new Error('Invalid file ID')

      const newMedia = constructEditObject(media, action.payload)
      const valid = newMedia.every(
        (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
      )

      return {
        ...state,
        media: newMedia,
        valid,
      }
    }
    case actions.setMediaGenerating: {
      const { id, count } = action.payload || {}
      if (!id) return state

      return {
        ...state,
        media: state.media.map((file) =>
          String(file.id) === String(id) ? { ...file, generating: true } : file,
        ),
        generatedMediaCount: count ?? state.generatedMediaCount,
      }
    }
    case actions.clearMediaGenerating: {
      return {
        ...state,
        media: state.media.map((file) => (file.generating ? { ...file, generating: false } : file)),
        generatedMediaCount: 0,
      }
    }
    case actions.bulkUpdateMediaItems: {
      const updatesById = new Map(action.payload.map((update) => [update.id, update]))

      return {
        ...state,
        media: state.media.map((file) =>
          updatesById.has(file.id) ? { ...file, ...updatesById.get(file.id) } : file,
        ),
      }
    }
    case actions.fileUploadInitiate: {
      const newFiles = state.media.map((file) => {
        if (file.id === action.payload.id) {
          return { ...file, ...action.payload, status: fileStatus.uploading, error: '' }
        }
        return file
      })

      return {
        ...state,
        media: newFiles,
        isLoading: true,
      }
    }
    case actions.fileUploadFail: {
      const newFiles = state.media.map((file) => {
        if (file.id === action.payload.id) {
          let newFile = file

          return {
            ...newFile,
            status: fileStatus.uploadFailed,
            error: i18n.t('Uploads.errors.fileUploadFailed'),
          }
        }
        return file
      })
      return {
        ...state,
        media: newFiles,
        isLoading: false,
        success: false,
        error: i18n.t('Uploads.errors.uploadFailed'),
      }
    }
    case actions.fileUploadSuccess: {
      const uploadedFileId = action.payload.id

      const newFiles = state.media.map((file) => {
        if (file.id === uploadedFileId) {
          return { ...file, status: fileStatus.success, error: '' }
        }
        return file
      })

      let nextState = {
        ...state,
        media: newFiles,
        failedReplaceByNewId: (() => {
          const { [uploadedFileId]: removedEntry, ...restFailedReplaces } =
            state.failedReplaceByNewId ?? {}
          void removedEntry
          return restFailedReplaces
        })(),
      }

      // if we have a file to replace, we need to clear it after the upload is successful
      if (state.fileToReplace && uploadedFileId === state.fileToReplace.id) {
        URL.revokeObjectURL(state.fileToReplace?.preview)
        nextState = {
          ...nextState,
          fileToReplace: null,
        }
      }

      return nextState
    }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

export const asyncActionHandlers = {
  [actions.addFilesAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { tc, tcRunId, eventId } = getState()
      const media = [action.payload].flat()

      const validationResult = await Promise.all(media.map((file) => validateFileAsync(file)))

      const newFiles = await Promise.all(
        validationResult.map((result) => {
          const [file, error] = result
          return constructFileObject(file, error)
        }),
      )

      dispatch({ type: actions.addFile, payload: newFiles })

      if (newFiles.every((x) => x.status === fileStatus.invalid)) return

      const filesToUpload = newFiles.filter((x) => x.status === fileStatus.new)

      await Promise.all(
        filesToUpload.map((file) => uploadFile(dispatch, file, { tc, tcRunId, eventId })),
      )
    },
  [actions.removeFileAsync]:
    ({ dispatch }) =>
    async (action) => {
      const file = action.payload

      abortUpload(file)
      dispatch({ type: actions.removeFile, payload: file })
    },
  [actions.replaceFileAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const state = getState()
      const { tc, tcRunId, eventId } = state
      const fileToReplace = state.fileToReplace
      if (!fileToReplace) throw new Error('Missing or invalid fileToReplace')

      const newFile = [action.payload].flat()[0]
      const [file, error] = await validateFileAsync(newFile)

      if (error) {
        dispatch({ type: actions.resetReplace, payload: { error } })
        return
      }

      const fileToUpload = await constructFileObject(file, error)

      if (fileToReplace.placement_type && fileToUpload.placement_type) {
        if (fileToReplace.placement_type !== fileToUpload.placement_type) {
          const errorKey =
            fileToReplace.placement_type === 'feed'
              ? 'Recommendations.campaign.setup.media.errors.feedReplace'
              : 'Recommendations.campaign.setup.media.errors.reelReplace'

          dispatch({ type: actions.resetReplace, payload: { error: i18n.t(errorKey) } })
          return
        }
      }

      const preparedFile = {
        ...fileToUpload,
        sub_group_id: fileToReplace.sub_group_id ?? null,
        headline: null,
        is_generated: false,
      }

      dispatch({
        type: actions.replaceFile,
        payload: buildReplaceFilePayload(preparedFile, fileToReplace),
      })

      try {
        await uploadFile(dispatch, preparedFile, { tc, tcRunId, eventId })
      } catch (error) {
        dispatch({
          type: actions.resetReplace,
          payload: { error: error?.message || 'File upload failed' },
        })
        return
      }
    },
  [actions.retryUploadAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { tc, tcRunId, eventId } = getState()
      const file = action.payload
      await uploadFile(dispatch, file, { tc, tcRunId, eventId })
    },
  [actions.addFileToGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { file, targetFileId, requiredPlacementType } = action.payload
      const state = getState()

      // Find target file
      const targetFile = state.media.find((x) => x.id === targetFileId)
      if (!targetFile) throw new Error('Target file not found')

      // Validate file
      const [validatedFile, error] = await validateFileAsync(file)
      if (error) {
        throw new Error(error)
      }

      // Create file object (this will calculate placement_type)
      const fileToUpload = await constructFileObject(validatedFile, error)

      if (fileToUpload.placement_type !== requiredPlacementType) {
        throw new Error(
          `File aspect ratio doesn't match required placement type. Expected: ${requiredPlacementType}, got: ${fileToUpload.placement_type}`,
        )
      }

      fileToUpload.insertAfterId = targetFileId

      dispatch({ type: actions.addFile, payload: [fileToUpload] })

      // Upload the new file
      await uploadFile(dispatch, fileToUpload, {
        tc: state.tc,
        tcRunId: state.tcRunId,
        eventId: state.eventId,
      })
    },
  [actions.replaceFileInGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { file, fileToReplaceId, requiredPlacementType } = action.payload
      const state = getState()

      // Find file to replace
      const fileToReplace = state.media.find((x) => x.id === fileToReplaceId)
      if (!fileToReplace) throw new Error('File to replace not found')

      // Store old file for potential restoration
      dispatch({ type: actions.startReplace, payload: fileToReplace })

      const [validatedFile, error] = await validateFileAsync(file)
      if (error) {
        // Restore old file on validation error
        dispatch({ type: actions.resetReplace, payload: { error } })
        return
      }

      // Create file object (this will calculate placement_type)
      const fileToUpload = await constructFileObject(validatedFile, error)

      if (fileToUpload.placement_type !== requiredPlacementType) {
        dispatch({
          type: actions.resetReplace,
          payload: {
            error: `File aspect ratio doesn't match required placement type. Expected: ${requiredPlacementType}, got: ${fileToUpload.placement_type}`,
          },
        })
        return
      }

      dispatch({
        type: actions.replaceFile,
        payload: buildReplaceFilePayload(fileToUpload, fileToReplace),
      })

      try {
        // Upload the new file
        await uploadFile(dispatch, fileToUpload, {
          tc: state.tc,
          tcRunId: state.tcRunId,
          eventId: state.eventId,
        })
      } catch (error) {
        // If upload throws, restore old file and surface error
        dispatch({
          type: actions.resetReplace,
          payload: { error: error?.message || 'File upload failed' },
        })
        return
      }
    },
  [actions.removeGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { groupId } = action.payload
      const state = getState()

      const filesInGroup = state.media.filter(
        (file) => file.placement_group_id === groupId || file.sub_group_id === groupId,
      )

      filesInGroup.forEach((file) => {
        abortUpload(file)
      })

      dispatch({ type: actions.removeFiles, payload: filesInGroup })
    },
  [actions.removeFromGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { fileId } = action.payload
      const state = getState()

      const fileToRemove = state.media.find((x) => x.id === fileId)
      if (!fileToRemove) throw new Error('File not found')

      if (!fileToRemove.placement_group_id && !fileToRemove.sub_group_id) {
        return dispatch({ type: actions.removeFileAsync, payload: fileToRemove })
      }

      abortUpload(fileToRemove)
      dispatch({ type: actions.removeFile, payload: fileToRemove })
    },
  [actions.removeMediaGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { groupId } = action.payload
      const state = getState()
      const filesInGroup = state.media.filter((file) => isFileInCoCreateGroup(file, groupId))

      filesInGroup.forEach((file) => {
        abortUpload(file)
      })

      dispatch({ type: actions.removeMediaGroup, payload: { groupId } })
    },
  [actions.removeFileFromCoCreateGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const { fileId } = action.payload
      const state = getState()
      const fileToRemove = state.media.find((x) => x.id === fileId)

      if (!fileToRemove) {
        throw new Error('File not found')
      }

      const groupId = fileToRemove.sub_group_id
      const remainingInGroup = state.media.filter(
        (file) => isFileInCoCreateGroup(file, groupId) && file.id !== fileId,
      )

      abortUpload(fileToRemove)

      if (remainingInGroup.length === 0) {
        dispatch({ type: actions.removeMediaGroup, payload: { groupId } })
      } else {
        dispatch({ type: actions.removeFile, payload: fileToRemove })
      }
    },
  [actions.addFileToCoCreateGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const {
        file,
        groupId,
        requiredRatio,
        requiredSlotRatios,
        originalId: providedOriginalId,
        isGoogleOnly,
        isGoogleSelected,
        googleExactRatios,
      } = action.payload
      const state = getState()
      const groupImages = state.media.filter((item) => isFileInCoCreateGroup(item, groupId))

      const validationError = await validateMediaFileAsync(file, {
        isGoogleOnly,
        isGoogleSelected,
        googleExactRatios,
        targetRatio: requiredRatio,
        requiredSlotRatios,
        t: i18n.t,
      })
      if (validationError) {
        throw new Error(validationError)
      }

      const fileToUpload = await constructFileObject(file, null)

      if (!groupImages.length) {
        if (!providedOriginalId) {
          throw new Error('CoCreate group not found')
        }

        const original = state.media.find((item) => item.id === providedOriginalId)

        if (!original) {
          throw new Error('CoCreate group not found')
        }

        fileToUpload.sub_group_id = original.sub_group_id ?? groupId
        fileToUpload.headline = null
        fileToUpload.is_generated = false

        dispatch({ type: actions.addFile, payload: [fileToUpload] })
        await uploadFile(dispatch, fileToUpload, {
          tc: state.tc,
          tcRunId: state.tcRunId,
          eventId: state.eventId,
        })
        return
      }

      const anchorFile = groupImages[0]

      if (
        groupImages.some(
          (item) => resolveImageSlotRatioForRequired(item, requiredSlotRatios) === requiredRatio,
        )
      ) {
        throw new Error(`Slot ${requiredRatio} is already filled`)
      }

      fileToUpload.sub_group_id = anchorFile.sub_group_id ?? groupId
      fileToUpload.headline = anchorFile.headline ?? null
      fileToUpload.is_generated = false

      dispatch({ type: actions.addFile, payload: [fileToUpload] })
      await uploadFile(dispatch, fileToUpload, {
        tc: state.tc,
        tcRunId: state.tcRunId,
        eventId: state.eventId,
      })
    },
  [actions.replaceFileInCoCreateGroupAsync]:
    ({ dispatch, getState }) =>
    async (action) => {
      const {
        file,
        fileToReplaceId,
        requiredRatio,
        requiredSlotRatios,
        isGoogleOnly,
        isGoogleSelected,
        googleExactRatios,
      } = action.payload
      const state = getState()
      const fileToReplace = state.media.find((x) => x.id === fileToReplaceId)

      if (!fileToReplace) {
        throw new Error('File to replace not found')
      }

      const validationError = await validateMediaFileAsync(file, {
        isGoogleOnly,
        isGoogleSelected,
        googleExactRatios,
        targetRatio: requiredRatio,
        requiredSlotRatios,
        t: i18n.t,
      })
      if (validationError) {
        throw new Error(validationError)
      }

      dispatch({ type: actions.startReplace, payload: fileToReplace })

      const fileToUpload = await constructFileObject(file, null)

      const preparedFile = {
        ...fileToUpload,
        sub_group_id: fileToReplace.sub_group_id ?? null,
        headline: null,
        is_generated: false,
      }

      dispatch({
        type: actions.replaceFile,
        payload: buildReplaceFilePayload(preparedFile, fileToReplace),
      })

      try {
        await uploadFile(dispatch, preparedFile, {
          tc: state.tc,
          tcRunId: state.tcRunId,
          eventId: state.eventId,
        })
      } catch (error) {
        dispatch({
          type: actions.resetReplace,
          payload: { error: error?.message || 'File upload failed' },
        })
      }
    },
}

const abortUpload = (file) => {
  if (file.status === fileStatus.uploading) {
    file.cancelTokenSource.cancel()
    abortMultipartUpload(
      {
        upload_id: file.uploadId,
        object_key: file.objectKey,
      },
      { upload_type: 'media' },
    )
  }
}

const uploadFile = async (dispatch, file, campaignContext) => {
  if (file.status === fileStatus.new) {
    await uploadNewFile(dispatch, file, campaignContext)
  } else if (file.status === fileStatus.uploadFailed) {
    const {
      content: { isValid },
    } = await validateUploadId(
      {
        upload_id: file.uploadId,
        object_key: file.objectKey,
        expected_total_parts: file.expectedTotalParts,
      },
      { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
    )
    if (!isValid) await uploadNewFile(dispatch, file, campaignContext)
    else {
      const presignedUrls = await getPresignedUrls(dispatch, file)
      await uploadParts(dispatch, file, presignedUrls, campaignContext)
    }
  }
}

const uploadNewFile = async (dispatch, file, campaignContext) => {
  const initialUploadData = await initiateUpload(dispatch, file)

  if (!initialUploadData) return

  const newFile = { ...file, ...initialUploadData }
  const presignedUrls = await getPresignedUrls(dispatch, newFile)

  await uploadParts(dispatch, newFile, presignedUrls, campaignContext)
}

const initiateUpload = async (dispatch, file) => {
  let { content: initialUploadData, error } = await initiateMultipartUpload(
    {
      object_name: file.name,
      content_type: file.type,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  if (error) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    return
  }

  initialUploadData = {
    uploadId: initialUploadData.upload_id,
    objectKey: initialUploadData.object_key,
  }

  dispatch({ type: actions.fileUploadInitiate, payload: { ...initialUploadData, id: file.id } })

  return initialUploadData
}

const getPresignedUrls = async (dispatch, file) => {
  const { content: presignedUrls, error } = await createPresignedUrl(
    {
      upload_id: file.uploadId,
      object_key: file.objectKey,
      start_part_number: file.totalUploadedParts + 1,
      total_parts: file.expectedTotalParts,
      total_uploaded_parts: file.totalUploadedParts,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  if (error) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    return
  }

  return presignedUrls
}

const uploadParts = async (dispatch, file, presignedUrls, campaignContext) => {
  let totalUploadedParts = file.totalUploadedParts
  let expectedTotalParts = file.expectedTotalParts
  let partsData = file.partsData

  if (!presignedUrls) return

  for (let index = 0; index < expectedTotalParts; index++) {
    const offset = index * PART_SIZE
    const part = file.file.slice(offset, offset + PART_SIZE)
    const presignedUrl = presignedUrls[index] || ''
    let lastPercentCompleted = 0
    const {
      content: partUploadResult,
      success,
      error,
    } = await callPresignedUrl(
      presignedUrl,
      part,
      { cancelToken: file.cancelTokenSource.token },
      (progress) => {
        if (progress > lastPercentCompleted) {
          lastPercentCompleted = progress
          dispatch({ type: actions.editFileInfo, payload: { id: file.id, progress } })
        }
      },
    )

    if (success) {
      partsData = [...partsData, { part_number: Number(index) + 1, etag: partUploadResult.etag }]
      totalUploadedParts = totalUploadedParts + 1

      dispatch({
        type: actions.editFileInfo,
        payload: { id: file.id, partsData, totalUploadedParts },
      })
    }

    if (error) {
      dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    }
  }

  if (totalUploadedParts !== expectedTotalParts) return

  const {
    success: uploadCompleted,
    error: uploadCompletionFailed,
    content,
  } = await completeMultipartUpload(
    {
      upload_id: file.uploadId,
      object_key: file.objectKey,
      parts: partsData,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  dispatch({ type: actions.editFileInfo, payload: { id: file.id, url: content.location } })

  if (uploadCompleted) {
    dispatch({ type: actions.fileUploadSuccess, payload: { id: file.id } })

    saveLibraryImages({
      filename: file.name,
      url: content.location,
      size: file.size,
      height: file.height,
      width: file.width,
      original: true,
      ratio: file.ratio,
      group_id: file.id,
      eid: campaignContext?.eventId,
      tc: campaignContext?.tc,
      tc_run_id: campaignContext?.tcRunId,
    })
  }
  if (uploadCompletionFailed) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
  }
}

const constructFileObject = async (file, error = null) => {
  let expectedTotalParts = Math.ceil(file.size / PART_SIZE, PART_SIZE)

  const dimensions = await getMediaDimensions(file)
  const fileName = file.path || file.name

  const placement_type = inferPlacementType(dimensions)
  const ratio = computeAspectRatio(dimensions.width, dimensions.height)

  return {
    file,
    filename: fileName,
    path: fileName,
    name: file.name,
    type: file.type,
    size: file.size,
    height: dimensions.height,
    width: dimensions.width,
    ratio,
    headline: null,
    is_generated: false,
    generating: false,
    sub_group_id: v4(),
    placement_type,
    id: v4(),
    error: error ? error : null,
    partsData: [],
    expectedTotalParts: expectedTotalParts,
    totalUploadedParts: 0,
    uploadId: null,
    objectKey: null,
    cancelTokenSource: axios.CancelToken.source(),
    progress: 0,
    preview: URL.createObjectURL(file),
    url: null,
    status: error ? fileStatus.invalid : fileStatus.new,
  }
}

const constructEditObject = (media, payload) => {
  return media.map((file) => {
    if (file.id === payload.id && payload.progress) {
      const progress = Math.round(
        (1 / file.expectedTotalParts) * (file.totalUploadedParts * 100 + payload.progress),
      )
      payload = { ...payload, progress }
    }
    if (file.id === payload.id) {
      return { ...file, ...payload }
    }

    return file
  })
}

// Minimum pixel size per canonical ratio bucket. Applies unconditionally,
// on every platform — not just Google, even though it originated from
// Google's published asset specs.
const MIN_DIMENSIONS_BY_RATIO = {
  '1.91:1': { width: 600, height: 314 },
  '1:1': { width: 300, height: 300 },
  '4:5': { width: 480, height: 600 },
  '9:16': { width: 600, height: 1067 },
}

// Reel/story-shaped media (matches the existing feed/reel placement-type
// heuristic elsewhere in this file) has its own maximum, independent of the
// ratio-bucket min-dimensions table above.
const VERTICAL_ASPECT_RANGE = [0.5, 0.575]
const VERTICAL_MAX_DIMENSIONS = { width: 1080, height: 1920 }
const FEED_VIDEO_MAX_DURATION_SEC = 240 * 60
const VERTICAL_VIDEO_MAX_DURATION_SEC = 2 * 60

const isVerticalShape = (width, height) =>
  Boolean(width && height && height > width) &&
  (() => {
    const aspectRatio = width / height
    return aspectRatio >= VERTICAL_ASPECT_RANGE[0] && aspectRatio <= VERTICAL_ASPECT_RANGE[1]
  })()

// Single validator for every upload/library/slot entry point: format, Google
// video support, size, resolution bounds, video duration, min-dimensions per
// ratio bucket, and (when targetRatio is given) ratio-match against a slot.
// Pure/sync — callers resolve width/height/duration themselves (raw Files go
// through validateMediaFileAsync below; already-hosted library items already
// carry their dimensions).
export function validateMediaFile(
  { type, size, width, height, duration, fileName },
  {
    isGoogleOnly = false,
    isGoogleSelected = false,
    googleExactRatios = [],
    targetRatio = null,
    requiredSlotRatios = null,
    t = i18n.t,
  } = {},
) {
  const isImage = type?.startsWith('image/')
  const isVideo = type?.startsWith('video/')

  if (!isImage && !isVideo) {
    return t('common.errors.fileFormat')
  }

  if (isGoogleOnly && isVideo) {
    return t('Recommendations.campaign.setup.media.errors.googleVideoUnsupported', {
      fileNames: fileName,
    })
  }

  if (isImage && !['image/jpeg', 'image/png'].includes(type)) {
    return t('common.errors.fileErrors.imageType', { fileName })
  }

  if (isVideo && !['video/mp4', 'video/quicktime'].includes(type)) {
    return t('common.errors.fileErrors.videoType', { fileName })
  }

  if (isImage && size > constants.MAX_IMG_UPLOAD_SIZE * 1024 * 1024) {
    return t('common.errors.fileErrors.imageSize', { fileName })
  }

  if (isVideo && size > constants.MAX_VIDEO_UPLOAD_SIZE * 1024 * 1024) {
    return t('common.errors.fileErrors.videoSize', { fileName })
  }

  const vertical = isVerticalShape(width, height)

  if (
    vertical &&
    (width > VERTICAL_MAX_DIMENSIONS.width || height > VERTICAL_MAX_DIMENSIONS.height)
  ) {
    return t(
      isImage
        ? 'common.errors.fileErrors.imageResolution'
        : 'common.errors.fileErrors.videoResolution',
      { fileName },
    )
  }

  if (isVideo && duration != null) {
    if (duration > FEED_VIDEO_MAX_DURATION_SEC) {
      return t('common.errors.fileErrors.videoDurationFeed', { fileName })
    }

    if (vertical && duration > VERTICAL_VIDEO_MAX_DURATION_SEC) {
      return t('common.errors.fileErrors.videoDurationReelStory', { fileName })
    }
  }

  const candidateRatios = requiredSlotRatios ?? MEDIA_ASPECT_RATIOS

  // Slot fit is checked before the per-ratio minimums below, because those
  // minimums are looked up by the slot's ratio: a landscape file dropped on a
  // 9:16 slot would otherwise be rejected for being under the portrait
  // minimum (600x1067) rather than for being the wrong shape for the slot.
  if (
    targetRatio &&
    width &&
    height &&
    !isFileRatioValidForSlot(width, height, targetRatio, candidateRatios)
  ) {
    return t('Recommendations.campaign.setup.media.errors.image.ratioSlot', {
      fileName,
      requiredRatio: targetRatio,
      uploadedRatio: computeAspectRatio(width, height),
    })
  }

  if (isImage && width && height) {
    const ratio = targetRatio ?? findClosestRatioLabel(width, height, candidateRatios)
    const min = ratio ? MIN_DIMENSIONS_BY_RATIO[ratio] : null

    if (min && (width < min.width || height < min.height)) {
      return t('Recommendations.campaign.setup.media.errors.image.minDimensions', {
        fileName,
        requiredRatio: ratio,
        minWidth: min.width,
        minHeight: min.height,
      })
    }

    // Google requires a pixel-exact match for its own ratios (not just
    // nearest-match) — applies everywhere, including an untargeted upload
    // (targetRatio absent), where `ratio` is the nearest canonical bucket
    // the image would land in anyway. Ratios outside googleExactRatios
    // (e.g. Display's 9:16, present only for Meta's Reels placement) keep
    // the nearest-match check below instead.
    if (
      isGoogleSelected &&
      ratio &&
      googleExactRatios.includes(ratio) &&
      !isExactRatioMatch({ width, height }, ratio)
    ) {
      // Slot-targeted uploads know exactly which single ratio they're
      // aiming for, so naming it is unambiguous. An untargeted upload (the
      // top-level dropzone/library-as-new-media) has no single target — a
      // message naming only the nearest bucket would wrongly imply that's
      // the *only* acceptable ratio, so list every ratio Google actually
      // requires exact, plus (when relevant) the Meta-only Reels ratio that
      // required-slot-ratios includes but Google itself doesn't need exact.
      if (targetRatio) {
        return t('Recommendations.campaign.setup.media.errors.image.exactRatioRequired', {
          fileName,
          requiredRatio: ratio,
        })
      }

      const extraRatios = (requiredSlotRatios ?? []).filter((r) => !googleExactRatios.includes(r))

      return t('Recommendations.campaign.setup.media.errors.image.exactRatioRequiredGeneric', {
        fileName,
        validRatios: googleExactRatios.join(', '),
        extraRatios: extraRatios.join(', '),
        context: extraRatios.length > 0 ? 'withReel' : undefined,
      })
    }
  }

  return null
}

// Convenience wrapper for a raw File — resolves width/height/duration via
// getMediaDimensions, then delegates to validateMediaFile.
export async function validateMediaFileAsync(file, options) {
  const dimensions = await getMediaDimensions(file)

  return validateMediaFile(
    {
      type: file.type,
      size: file.size,
      width: dimensions.width,
      height: dimensions.height,
      duration: dimensions.duration,
      fileName: file.name,
    },
    options,
  )
}

async function validateFileAsync(file) {
  const fileSizeMB = Math.round(file.size / 1000 / 1000)
  let error

  if (file.type.startsWith('image')) {
    if (fileSizeMB > constants.MAX_IMG_UPLOAD_SIZE) {
      error = i18n.t('common.errors.imgSize', { size: constants.MAX_IMG_UPLOAD_SIZE.toFixed(1) })
    }
  } else if (file.type.startsWith('video')) {
    const video = document.createElement('video')
    video.src = window.URL.createObjectURL(file)

    await new Promise((resolve) => {
      video.addEventListener('loadedmetadata', () => {
        window.URL.revokeObjectURL(video.src)
        resolve()
      })
    })

    if (fileSizeMB > constants.MAX_VIDEO_UPLOAD_SIZE) {
      error = i18n.t('common.errors.videoSize', {
        size: constants.MAX_VIDEO_UPLOAD_SIZE.toFixed(1) / 1000,
      })
    }
  } else {
    error = i18n.t('common.errors.fileFormat')
  }

  return [file, error]
}

export function allFilesUploaded(media) {
  // A youtube item is a reference, not an upload — it never goes through the
  // upload pipeline, so it never gets a status (see normalizeIncomingMediaItem
  // in mediaPayload.js) and must not block the "everything's uploaded" check.
  return media.every((x) => x.youtube_video_id || x.status === fileStatus.success)
}
